import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  IMyNoosSearchQuery,
  MyNoosArticleSearchResult,
  MyNoosVariantsSearchResult,
  SearchResultV2,
} from '@epicuro-next/models/products';
import {
  Product,
  ProductKey,
  SearchQuery,
} from 'graphql-api/gql-api-interfaces';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import {
  IProductAvailability,
  IProductAvailabilityQueryParams,
  IProductReferenceQueryParams,
  ISizeRun,
  ISizeRunsFilters,
} from '../models';
import { PricesGQL, ProductGQL, SearchGQL } from '../queries';
import { FindSlimProductGQL } from '../queries/slim-product.gql';
import {
  PRODUCTS_API_TOKEN,
  PRODUCTS_SEARCH_API_TOKEN,
  SIZE_RUNS_API_TOKEN,
} from '../tokens';

@Injectable()
export class ProductService {
  constructor(
    private productGQL: ProductGQL,
    private pricesGQL: PricesGQL,
    private searchGql: SearchGQL,
    private slimProductGQL: FindSlimProductGQL,
    @Inject(PRODUCTS_API_TOKEN) private productsApiUrl: string,
    @Inject(PRODUCTS_SEARCH_API_TOKEN) private productsSearchApiUrl: string,
    @Inject(SIZE_RUNS_API_TOKEN) private sizerunsApiUrl: string,
    private httpClient: HttpClient,
  ) {}

  public loadProduct(
    productId: string,
    brandId: string,
    loadPrices: boolean,
    loadTrueDemand = false,
  ) {
    return this.productGQL
      .fetch({
        productId,
        brandId,
        loadPrices,
        loadTrueDemand,
      })
      .pipe(
        take(1),
        map(({ data }) => data.product),
      );
  }

  public loadSlimProduct(
    productId: string,
    brandId: string,
    includeRelatedProducts = false,
  ) {
    return this.slimProductGQL
      .fetch({
        productId,
        brandId,
        includeRelatedProducts,
      })
      .pipe(
        take(1),
        map(({ data }) => data.findSlimProduct),
      );
  }

  public loadPrices(products: ProductKey[], type: string) {
    return this.pricesGQL
      .fetch({
        products,
        type,
      })
      .pipe(
        take(1),
        map(({ data }) => data.prices),
      );
  }

  public productsByReferences(query: SearchQuery) {
    return this.searchGql
      .fetch({
        query: {
          ...query,
          useV2Search: true,
        },
      })
      .pipe(
        map((queryResult) => ({
          products: (queryResult.data.search?.products as Product[]) ?? [],
          totalResultCount: queryResult.data.search?.totalResultCount ?? 0,
        })),
      );
  }

  public loadProductsByReferences(queryParams: IProductReferenceQueryParams) {
    return this.httpClient
      .post<SearchResultV2>(`${this.productsSearchApiUrl}`, queryParams)
      .pipe(map(({ products }) => products));
  }

  public getOrderProducts(
    brandId: string,
    accountId: string,
    productIds: string[],
  ): Observable<object> {
    return this.httpClient.post<object>(
      `${this.productsApiUrl}/${accountId}/${brandId}/orderbook`,
      {
        productIds: productIds.filter(Boolean),
      },
    );
  }

  public checkProductsAvailabilityByReferences(
    accountId: string,
    body: IProductAvailabilityQueryParams,
  ) {
    return this.httpClient.post<IProductAvailability>(
      `${this.productsApiUrl}/${accountId}/check-availability`,
      body,
    );
  }

  public getSizeRuns(body: ISizeRunsFilters) {
    return this.httpClient.post<ISizeRun>(`${this.sizerunsApiUrl}/match`, {
      filters: body,
    });
  }

  public getMyNoos(
    accountId: string,
    { facets, ...rest }: IMyNoosSearchQuery,
  ): Observable<MyNoosVariantsSearchResult | MyNoosArticleSearchResult> {
    const facetsParam = facets
      .map((filter) => {
        return `${filter.fieldId}${':'}${filter.facetValues
          ?.map(({ value }) => value)
          ?.join('|')}`;
      })
      .join(',');

    const body: Record<string, unknown> = {
      accountId,
      ...rest,
    };

    if (facetsParam) {
      body.facets = facetsParam;
    }

    return this.httpClient.post<
      MyNoosVariantsSearchResult | MyNoosArticleSearchResult
    >(`${this.productsApiUrl}/${accountId}/mynoos`, body);
  }
}
